import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { PENDING, IDLE } from "const"
import { MtUser } from "server/models"
import { deepCopy, StateType } from "client/utils"
import {
    StoreType,
    ProjectGroupUserProps,
    ProjectUserList,
    ProjectGroupUserList,
    UserFilters,

    LoadProjectUsers,
    LoadProjectGroupUsers,
    SortUsers,
    DeleteProjectUserGroup,

    setProjectUserList,
    setProjectGroupUserList,
    setAssignedProjectGroupUserIds,
    setAssignedProjectGroupUserSort,
    setAssignedProjectGroupUserFilters,
    setNotAssignedProjectGroupUserSelected,
    addModal,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    Loader,
    Button,
    TableSearchWrapper,
    Table,
    SortRows,
    DeleteRow,
} from "client/components"

import UserSearch from "client/pages/search/userSearch"
import NotAssignedUsers from "../notAssigned"
import Columns from "./columns"

const AssignedUsers = ({
    group,
}: ProjectGroupUserProps) => {
    const userList = useSelector((state: StoreType) => state.user.userList)
    const groupUserList = useSelector((state: StoreType) => state.projectUserGroup.groupUserList)
    const groupUserIds = (groupUserList && groupUserList[group.groupId]) || []

    const userIds = useSelector((state: StoreType) => state.projectUserGroup.assignedUsers.userIds)
    const sortColumn = useSelector((state: StoreType) => state.projectUserGroup.assignedUsers.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.projectUserGroup.assignedUsers.sortOrder)
    const filters = useSelector((state: StoreType) => state.projectUserGroup.assignedUsers.filters)
    const reloadList = useSelector((state: StoreType) => state.projectUserGroup.reloadGroupUserList)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    useEffect(() => {
        if (!reloadList) return
        setLoadState(PENDING)
        Promise.all([
            LoadProjectGroupUsers(group.groupId, group.accountId, group.projectId, userList)
            .then((assignedUserIds) => {
                const groupUserListCopy: ProjectGroupUserList = groupUserList ? deepCopy(groupUserList) : {}
                groupUserListCopy[group.groupId] = assignedUserIds
                dispatch(setProjectGroupUserList(groupUserListCopy))
            }),
            LoadProjectUsers(group.projectId).then((projectUsers) => {
                const newProjectUserList: ProjectUserList = { [group.projectId]: projectUsers }
                dispatch(setProjectUserList(newProjectUserList))
            }),
        ]).then(() => {
            setLoadState(IDLE)
        })
    }, [reloadList])

    useEffect(() => {
        const sortedUserIds = SortUsers(Object.values(userList), sortColumn, sortOrder, filters)
        const sortedAssigned = sortedUserIds.filter((userId) => groupUserIds.includes(userId))
        dispatch(setAssignedProjectGroupUserIds(sortedAssigned))
    }, [groupUserList, sortColumn, sortOrder, filters])

    const onSort: SortRows = (column: keyof MtUser, order) => {
        dispatch(setAssignedProjectGroupUserSort(column, order))
    }

    const onFilter = (newFilters: UserFilters) => {
        dispatch(setAssignedProjectGroupUserFilters(newFilters))
    }

    const onDelete: DeleteRow = (user: MtUser, callback) => {
        DeleteProjectUserGroup(user.id, group.groupId, group.projectId, group.accountId).then((removedRows) => {
            const success = removedRows === 1
            callback(success, () => {
                const groupUserListCopy: ProjectGroupUserList = groupUserList ? deepCopy(groupUserList) : {}
                groupUserListCopy[group.groupId] = groupUserIds.filter((id) => id !== user.id)
                dispatch(setProjectGroupUserList(groupUserListCopy))
            })
        })
    }

    const columns = Columns()
    return (
        <Container>
            <PageHeader>
                <PageTitle>{`${group.name}: ${lang.assignedUsers}`}</PageTitle>
            </PageHeader>
            <PageContent>
                {loadState === PENDING && <Loader />}
                <TableSearchWrapper>
                    <UserSearch
                        id="assignedProjectGroupUsers"
                        rightOffset
                        filters={filters}
                        onFilter={onFilter}
                    />
                    <Button
                        variant="success"
                        onClick={() => {
                            dispatch(setNotAssignedProjectGroupUserSelected(null))
                            dispatch(addModal({
                                key: "notAssignedUsers",
                                content: <NotAssignedUsers group={group} />,
                            }))
                        }}
                    >
                        {lang.add}
                    </Button>
                </TableSearchWrapper>
                <Table
                    columns={columns}
                    dataList={userList}
                    dataIds={userIds}
                    sortColumn={sortColumn}
                    sortOrder={sortOrder}
                    rowSize="sm"
                    showPagination
                    onSort={onSort}
                    onDelete={onDelete}
                />
            </PageContent>
        </Container>
    )
}
export default AssignedUsers
